import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useAuth } from '../context/AuthContext';

interface ResendVerificationFormData {
  email: string;
}

export const ResendVerificationForm: React.FC = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [apiError, setApiError] = useState<string>('');
  const [success, setSuccess] = useState(false);
  const { resendVerification } = useAuth();
  const { register, handleSubmit, formState: { errors } } = useForm<ResendVerificationFormData>();

  const onSubmit = async (data: ResendVerificationFormData) => {
    setIsLoading(true);
    setApiError('');
    setSuccess(false);

    try {
      const result = await resendVerification(data.email);

      if (result.success) {
        setSuccess(true);
      } else {
        setApiError(result.error?.message || 'Failed to resend verification email');
      }
    } catch (error) {
      setApiError('An unexpected error occurred. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="scientia-container">
      <div className="scientia-card">
        <div className="scientia-card__content">
          <h2 className="title has-text-centered mb-4">Resend Verification Email</h2>

          {success ? (
            <div className="success-message">
              <p>A new verification email has been sent to your email address. Please check your inbox.</p>
              <p className="mt-2">If you don't see the email, please check your spam folder.</p>
              <div className="has-text-centered mt-4">
                <a href="/auth/login" className="button is-primary">Return to Login</a>
              </div>
            </div>
          ) : (
            <form onSubmit={handleSubmit(onSubmit)}>
              <div className="field">
                <label className="label" htmlFor="email">Email</label>
                <div className="form-control">
                  <input
                    type="email"
                    id="email"
                    autoComplete="email"
                    className={`input ${errors.email ? 'is-danger' : ''}`}
                    {...register('email', {
                      required: 'Email is required',
                      pattern: {
                        value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                        message: 'Invalid email address',
                      },
                    })}
                  />
                </div>
                {errors.email && (
                  <p className="help is-danger">{errors.email.message}</p>
                )}
              </div>

              {apiError && (
                <div className="form-message is-error" role="alert">
                  {apiError}
                </div>
              )}

              <div className="field">
                <div className="form-actions">
                  <button
                    type="submit"
                    className={`button is-primary is-fullwidth ${isLoading ? 'is-loading' : ''}`}
                    disabled={isLoading}
                  >
                    {isLoading ? 'Sending...' : 'Resend Verification Email'}
                  </button>
                </div>
              </div>

              <div className="has-text-centered mt-3">
                <a href="/auth/login" className="has-text-primary">
                  Back to Login
                </a>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};
